import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';
import { colors, spacing, typography, radius, shadows, transitions, zIndex } from '../../tokens';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string | React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'base' | 'lg' | 'xl' | 'full';
  height?: 'auto' | 'sm' | 'base' | 'lg' | 'xl' | 'full' | string;
  closeOnBackdropClick?: boolean;
  closeOnEsc?: boolean;
  showCloseButton?: boolean;
  className?: string;
}

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideIn = keyframes`
  from {
    transform: translate(-50%, -48%);
    opacity: 0;
  }
  to {
    transform: translate(-50%, -50%);
    opacity: 1;
  }
`;

const Backdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  z-index: ${zIndex.modal};
  animation: ${fadeIn} 150ms ease-out;
`;

const sizeMap = {
  sm: '400px',
  base: '500px',
  lg: '600px',
  xl: '1000px',
  full: '90vw',
};

const heightMap = {
  auto: 'auto',
  sm: '400px',
  base: '500px',
  lg: '600px',
  xl: '700px',
  full: '90vh',
};

const ModalContainer = styled.div<{ $size: string; $height?: string }>`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: ${colors.background.primary};
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
  max-width: ${props => sizeMap[props.$size as keyof typeof sizeMap]};
  width: 100%;
  height: ${props => {
    const height = props.$height || 'auto';
    // If it's a predefined height, use the map
    if (height in heightMap) {
      return heightMap[height as keyof typeof heightMap];
    }
    // If it's a custom string (like '600px'), use it directly
    if (height !== 'auto') {
      return height;
    }
    // Default auto behavior with max-height
    return 'auto';
  }};
  max-height: ${props => (props.$height || 'auto') === 'auto' ? '90vh' : 'none'};
  display: flex;
  flex-direction: column;
  z-index: ${zIndex.modal + 1};
  animation: ${slideIn} 200ms ease-out;
  font-family: ${typography.fontFamilies.sans};
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
  background: ${colors.background.primary};
`;

const ModalTitle = styled.div`
  margin: 0;
  font-family: ${typography.fontFamilies.sans};
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  line-height: 1.3;
  
  /* When title is just a string, it will be wrapped in a text node */
  /* When title is a React element, it will render as-is */
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  padding: ${spacing[2]};
  cursor: pointer;
  color: ${colors.text.secondary};
  border-radius: ${radius.md};
  transition: ${transitions.colors};
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: ${colors.background.hover};
    color: ${colors.text.primary};
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px ${colors.brand.primarySurface};
  }
`;

const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${spacing[8]};
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${spacing[3]};
  padding: ${spacing[6]} ${spacing[8]};
  border-top: 1px solid ${colors.border.secondary};
  background: ${colors.background.primary};
`;

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'base',
  height = 'auto',
  closeOnBackdropClick = true,
  closeOnEsc = true,
  showCloseButton = true,
  className,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (closeOnEsc && event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose, closeOnEsc]);

  const handleBackdropClick = (event: React.MouseEvent) => {
    if (closeOnBackdropClick && event.target === event.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      <Backdrop onClick={handleBackdropClick} />
      <ModalContainer ref={modalRef} $size={size} $height={height} className={className}>
        {(title || showCloseButton) && (
          <ModalHeader>
            {title && (
              <ModalTitle>
                {typeof title === 'string' ? title : title}
              </ModalTitle>
            )}
            {showCloseButton && (
              <CloseButton onClick={onClose} aria-label="Close modal">
                <CloseIcon />
              </CloseButton>
            )}
          </ModalHeader>
        )}
        <ModalBody>{children}</ModalBody>
        {footer && <ModalFooter>{footer}</ModalFooter>}
      </ModalContainer>
    </>,
    document.body
  );
}; 